import React from "react"
import { useFormikContext } from "formik"
import { Checkbox, FormControlLabel, InputAdornment } from "@mui/material"
import { Check, Mail, User } from "lucide-mui"
import i18n from "@/i18n"
import { TextField } from "@/components/form"
import { PRIMARY_BLUE, BORDER_INPUT } from "@/constants/colors"
import { type ReservationFormValues } from "@/pages/public/venue"
import PhoneInput from "../phone_input"

export type Table = {
  id: string
  name: string
  tableGroup?: {
    name?: string | null
  } | null
}

export interface GuestInformationStepProps {
  holdDurationSeconds?: number | null
  country?: string | null
  timezone?: string
  tables?: Table[]
  mobileSheet?: boolean
  onBack: () => void
  onClose?: () => void
  onSubmit: () => void
  isSubmitting: boolean
}

export const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#ffffff",
    minHeight: "42px",
    "& fieldset": { borderColor: BORDER_INPUT },
    "&:hover fieldset": { borderColor: "#dfe3e8" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY_BLUE },
  },
  "& .MuiOutlinedInput-input": {
    py: "8px",
    fontSize: "12px",
    lineHeight: "18px",
    color: "#808694",
  },
  "& .MuiInputAdornment-root": {
    color: "#808694",
  },
  "& .MuiInputAdornment-positionStart": {
    mr: "6px",
  },
} as const

export const NOTE_INPUT_SX = {
  ...INPUT_SX,
  "& .MuiOutlinedInput-input": {
    fontSize: "12px",
    lineHeight: "18px",
    color: "#414142",
  },
  "& .MuiOutlinedInput-inputMultiline": {
    minHeight: "56px !important",
  },
} as const

export const MOBILE_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    bgcolor: "#ffffff",
    minHeight: "40px",
    "& fieldset": { borderColor: BORDER_INPUT },
    "&:hover fieldset": { borderColor: "#dfe3e8" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY_BLUE },
  },
  "& .MuiOutlinedInput-input": {
    py: "9px",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#374151",
    "&::placeholder": {
      color: "#9ca3af",
      opacity: 1,
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#acb3c0",
  },
  "& .MuiInputAdornment-positionStart": {
    mr: "8px",
  },
} as const

export const MOBILE_NOTE_INPUT_SX = {
  ...MOBILE_INPUT_SX,
  "& .MuiOutlinedInput-root": {
    ...MOBILE_INPUT_SX["& .MuiOutlinedInput-root"],
    alignItems: "flex-start",
  },
  "& .MuiOutlinedInput-input": {
    ...MOBILE_INPUT_SX["& .MuiOutlinedInput-input"],
    minHeight: "18px",
  },
  "& .MuiOutlinedInput-inputMultiline": {
    minHeight: "36px !important",
  },
} as const

export const FIELD_LABEL_CLASS_NAME = "mb-1 block text-[12px] font-normal leading-[18px] text-[#414142]"
export const MOBILE_FIELD_LABEL_CLASS_NAME = "mb-1 block text-xs font-medium text-[#373942]"

const STYLES = {
  checkboxIcon: "block h-[18px] w-[18px] rounded-[4px] border border-[#d8dde5] bg-white",
  checkboxCheckedIcon:
    "flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border border-[#2596fa] bg-[#2596fa] text-white",
  formContainer: "space-y-4",
  desktopConsentText: "m-0 pt-[2px] text-[10px] leading-[13px] text-black",
  mobileConsentText: "m-0 pt-[1px] text-[10px] leading-[13px] text-[#3e3e3e]",
  desktopCheckboxGroup: "space-y-2.5 pt-2",
  mobileCheckboxGroup: "space-y-3 pt-1",
  checkboxRow: "items-start gap-2.5",
  termsRow: "flex items-start gap-2.5",
  mobilePrivacyLink: "text-[#2596fa] underline",
  desktopPrivacyLink: "text-[#2596fa] no-underline",
} as const

export const CHECKBOX_ICON = <span className={STYLES.checkboxIcon} />
export const CHECKBOX_CHECKED_ICON = (
  <span className={STYLES.checkboxCheckedIcon}>
    <Check sx={{ fontSize: 13 }} />
  </span>
)
export const CHECKBOX_SX = {
  p: 0.25,
  mt: 0.125,
  color: "transparent",
  "&.Mui-checked": {
    color: "transparent",
  },
  "&:hover": {
    bgcolor: "transparent",
  },
} as const

type GuestFormFieldsProps = {
  mobileSheet: boolean
  country?: string | null
}

export const GuestFormFields: React.FC<GuestFormFieldsProps> = ({ mobileSheet, country }) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<ReservationFormValues>()
  const fieldLabelClassName = mobileSheet ? MOBILE_FIELD_LABEL_CLASS_NAME : FIELD_LABEL_CLASS_NAME
  const inputSx = mobileSheet ? MOBILE_INPUT_SX : INPUT_SX
  const noteInputSx = mobileSheet ? MOBILE_NOTE_INPUT_SX : NOTE_INPUT_SX
  const consentTextClassName = mobileSheet ? STYLES.mobileConsentText : STYLES.desktopConsentText
  const noteLabel = mobileSheet
    ? i18n.t("app.public.venue.modal.special_notes")
    : i18n.t("app.public.venue.modal.special_requests")
  const marketingConsentLabel = [
    i18n.t("app.public.venue.modal.marketing_consent_prefix"),
    i18n.t("app.public.venue.modal.marketing_consent_emphasis"),
    i18n.t("app.public.venue.modal.marketing_consent_suffix"),
  ].join(" ")
  const termsAcceptedLabel = `${i18n.t("app.public.venue.modal.terms_prefix")} ${i18n.t("app.public.venue.modal.privacy_policy")}`

  return (
    <div className={STYLES.formContainer} data-testid="public-reservation-modal-guest-form">
      <div>
        <label className={fieldLabelClassName} htmlFor="name">
          {i18n.t("app.public.venue.modal.full_name")}
        </label>
        <TextField
          name="name"
          startAdornment={
            <InputAdornment position="start">
              <User sx={{ fontSize: 14 }} />
            </InputAdornment>
          }
          data-testid="public-reservation-modal-guest-name"
          InputProps={{ sx: inputSx }}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name ? errors.name : undefined}
        />
      </div>

      <div>
        <label className={fieldLabelClassName} htmlFor="phone">
          {i18n.t("app.public.venue.modal.phone_number")}
        </label>
        <PhoneInput name="phone" defaultCountry={country} mobileSheet={mobileSheet} />
      </div>

      <div>
        <label className={fieldLabelClassName} htmlFor="email">
          {i18n.t("app.public.venue.modal.email")}
        </label>
        <TextField
          name="email"
          type="email"
          startAdornment={
            <InputAdornment position="start">
              <Mail sx={{ fontSize: 14 }} />
            </InputAdornment>
          }
          data-testid="public-reservation-modal-guest-email"
          InputProps={{ sx: inputSx }}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email ? errors.email : undefined}
        />
      </div>

      <div>
        <label className={fieldLabelClassName} htmlFor="note">
          {noteLabel}
        </label>
        <TextField
          name="note"
          multiline
          minRows={mobileSheet ? 3 : 4}
          maxRows={6}
          data-testid="public-reservation-modal-guest-note"
          InputProps={{ sx: noteInputSx }}
          error={Boolean(touched.note && errors.note)}
          helperText={touched.note ? errors.note : undefined}
        />
      </div>

      <div className={mobileSheet ? STYLES.mobileCheckboxGroup : STYLES.desktopCheckboxGroup}>
        <FormControlLabel
          className={STYLES.checkboxRow}
          data-testid="public-reservation-modal-guest-marketing-consent"
          control={
            <Checkbox
              checked={values.marketingConsent}
              onChange={(event) => setFieldValue("marketingConsent", event.target.checked)}
              disableRipple
              size="small"
              icon={CHECKBOX_ICON}
              checkedIcon={CHECKBOX_CHECKED_ICON}
              sx={CHECKBOX_SX}
              slotProps={{ input: { "aria-label": marketingConsentLabel } }}
            />
          }
          label={
            <span className={consentTextClassName}>
              <span>{i18n.t("app.public.venue.modal.marketing_consent_prefix")}</span>
              <strong>{` ${i18n.t("app.public.venue.modal.marketing_consent_emphasis")} `}</strong>
              <span>{i18n.t("app.public.venue.modal.marketing_consent_suffix")}</span>
            </span>
          }
          sx={{ m: 0, alignItems: "flex-start" }}
        />

        <div className={STYLES.termsRow} data-testid="public-reservation-modal-guest-terms-accepted">
          <Checkbox
            checked={values.termsAccepted}
            onChange={(event) => setFieldValue("termsAccepted", event.target.checked)}
            disableRipple
            size="small"
            icon={CHECKBOX_ICON}
            checkedIcon={CHECKBOX_CHECKED_ICON}
            sx={CHECKBOX_SX}
            slotProps={{ input: { "aria-label": termsAcceptedLabel } }}
          />
          <span className={consentTextClassName}>
            <span>{`${i18n.t("app.public.venue.modal.terms_prefix")} `}</span>
            <a className={mobileSheet ? STYLES.mobilePrivacyLink : STYLES.desktopPrivacyLink} href="/privacy_policy">
              {i18n.t("app.public.venue.modal.privacy_policy")}
            </a>
          </span>
        </div>
      </div>
    </div>
  )
}
